/**
 * The finance analysis, as a spreadsheet or a printed sheet.
 *
 * Built from the FIGURES ALREADY ON SCREEN, not from a second request. Two
 * reasons, and the first is the important one:
 *
 *   · an export fetched separately can disagree with the page it was taken
 *     from. The aggregates are cached for five minutes and the row set moves as
 *     money comes in, so a re-query would quietly hand over a different report
 *     than the one somebody just read and decided to circulate;
 *   · there is nothing to deploy. The server has no filtered export endpoint —
 *     FinanceExportController is unrouted and dumps the whole payments table —
 *     and the portal talks to the production API, so a new route would not
 *     exist until the API shipped.
 *
 * Every sheet carries the FILTERS that produced it. A finance total with no
 * statement of what it covers is the one thing nobody can check a week later,
 * and this is a page whose every figure changes meaning with the source filter:
 * "Expected" is dues and the levy only, and is omitted entirely for a voluntary
 * fee. The sheets say so in the same words the screen does.
 *
 * Amounts are written in NAIRA, converted once here. The API hands over kobo
 * (`summary.unit`), and a spreadsheet of kobo labelled with a ₦ is how a figure
 * ends up a hundred times too large in a report to Council.
 */

/** Kobo → naira, as a plain number for a spreadsheet cell. */
const naira = (kobo: any) => Number(kobo || 0) / 100;

/** Kobo → naira, formatted, for a printed sheet. */
const money = (kobo: any) =>
  "₦" +
  naira(kobo).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export type FinanceExportMeta = {
  /** Human labels for the filters in force, e.g. { Year: "2026" }. Only set ones. */
  filters: Record<string, string>;
  /** True where the selected source can only be reported as collections. */
  receivedOnly: boolean;
  /** The source's label, when one is selected. */
  sourceLabel?: string | null;
};

export type FinanceExportData = {
  summary: any;
  bySource: any[];
  byStatus: any[];
  breakdown: any | null;
  sourceLabels: Record<string, string>;
  meta: FinanceExportMeta;
  /** The transactions behind the figures. Absent until the export fetches them. */
  payments?: any[] | null;
  paymentsTruncated?: boolean;
  /** Server-side sum of the allocated column, for the reconciliation note. */
  paymentsTotal?: number | null;
};

const day = (value: any) => (value ? String(value).slice(0, 10) : "");
const dayTime = (value: any) =>
  value ? String(value).slice(0, 16).replace("T", " ") : "";

const stamp = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");

  return {
    printed: d.toLocaleString("en-NG"),
    file: `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`,
  };
};

const slug = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "report";

/** RFC 4180: quote every field, and double any quote inside one. */
const cell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const line = (cells: unknown[]) => cells.map(cell).join(",");

const esc = (text: unknown) =>
  String(text ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" } as any)[c],
  );

const filename = (meta: FinanceExportMeta, extension: string) => {
  const parts = ["finance-analysis"];
  if (meta.sourceLabel) parts.push(slug(meta.sourceLabel));
  return `${parts.join("-")}-${stamp().file}.${extension}`;
};

const download = (content: string, type: string, name: string) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * The header both sheets open with: what this is a report OF.
 *
 * `[label, value]` pairs rather than a sentence, so the spreadsheet keeps them
 * in two readable columns.
 */
function headerRows(d: FinanceExportData): [string, string][] {
  const rows: [string, string][] = [];
  const entries = Object.entries(d.meta.filters);

  rows.push(["Filters", entries.length ? "" : "None — the whole period"]);
  entries.forEach(([k, v]) => rows.push([`  ${k}`, v]));

  rows.push(["Taken on", stamp().printed]);
  rows.push(["Amounts", "Naira"]);

  /* Which of the two reports this is. A date range makes it a statement of
     FLOW — what came in, by the date each payment was made — and the billed and
     owed figures are left out rather than printed beside receipts they cannot
     be subtracted from. */
  const receipts = d.summary?.basis === "receipts";

  rows.push([
    "Report",
    receipts
      ? "Money RECEIVED in the window, by the date each payment was made"
      : "Positions: billed, received and still owed, as they stand",
  ]);

  rows.push([
    "Expected / Outstanding",
    receipts
      ? "Not reported for a date range — they are positions, not a flow"
      : d.meta.receivedOnly
        ? "Not reported — a voluntary fee is not a debt, so only receipts are shown"
        : "Annual dues and the development levy only",
  ]);

  return rows;
}

/**
 * The transaction list, and the two figures that let it be reconciled.
 *
 * `Amount` is what the transaction was for in total; `Counted here` is the part
 * that landed inside the filter — one payment can settle 2026 and 2025 dues
 * together, and under a 2026 filter only the 2026 half is this report's money.
 * It is the second column that sums to Received.
 *
 * Where the two totals disagree it is said out loud rather than left to be
 * discovered: an invoice migrated from the old register can carry a paid amount
 * with no payment row behind it, so its money is in Received and has no
 * transaction to list.
 */
function paymentLines(d: FinanceExportData): string[] {
  const rows = d.payments ?? [];
  const listed = rows.reduce((sum, r) => sum + Number(r.allocated || 0), 0);
  const received = Number(d.summary?.total_paid || 0);

  const out: string[] = [
    line([`Payments (${rows.length.toLocaleString()})`]),
    line([
      "Paid on", "Reference", "Receipt no", "Payer", "Membership no",
      "Invoice", "Source", "Method", "Gateway", "Bank / account", "Year",
      "Amount", "Counted here",
    ]),
    ...rows.map((r: any) =>
      line([
        dayTime(r.paid_at),
        r.reference ?? "",
        r.receipt_number ?? "",
        r.payer ?? "",
        r.membership_no ?? "",
        r.invoice_number ?? "",
        d.sourceLabels[r.source] ?? r.source ?? "",
        r.method ?? "",
        r.gateway ?? "",
        r.bank ?? "",
        r.year ?? "",
        naira(r.amount),
        naira(r.allocated),
      ]),
    ),
    line(["", "", "", "", "", "", "", "", "", "", "Listed total", "", naira(listed)]),
  ];

  if (d.paymentsTruncated) {
    out.push(
      line([
        "Note",
        `The most recent ${rows.length.toLocaleString()} payments only — narrow the filters to list the rest. The totals above cover everything.`,
      ]),
    );
  }

  /* Only worth saying where the list is COMPLETE. A capped list is short for a
     reason the note above already gives, and blaming the old register for it
     would send somebody looking for a data fault that is not there. */
  if (!d.paymentsTruncated && Math.abs(listed - received) > 1) {
    out.push(
      line([
        "Note",
        "The listed payments do not add up to Received. Invoices migrated from the old "
          + "register can carry a paid amount with no payment record behind it, so that money "
          + "is counted in Received but has no transaction to list here.",
      ]),
    );
  }

  return out;
}

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

export function exportFinanceAnalysisCsv(d: FinanceExportData) {
  const s = d.summary ?? {};
  const receipts = s.basis === "receipts";
  const receivedOnly = receipts || d.meta.receivedOnly;
  const isEvents = d.breakdown?.source === "event_fee";

  const lines: string[] = [
    line(["NIQS — Finance Analysis"]),
    ...headerRows(d).map((r) => line(r)),
    "",
    line(["Summary"]),
    // A dash, not a zero: for a voluntary fee these are not applicable, and a
    // zero in a spreadsheet reads as "nothing expected" and will be summed.
    line(["Total expected", receivedOnly ? "—" : naira(s.total_expected ?? s.total_due)]),
    line(["Received", naira(s.total_paid)]),
    line(["Outstanding", receivedOnly ? "—" : naira(s.total_balance)]),
    line(["Collection rate (%)", receivedOnly ? "—" : (s.collection_rate ?? 0)]),
    ...(receipts ? [line(["Payments", s.payments ?? 0])] : []),
    line(["Invoices", s.invoices ?? 0]),
    line(["Members", s.members ?? 0]),
    "",
    line([receipts ? "Money received, by source" : "Income by source"]),
    line([
      "Source",
      ...(receipts ? ["Payments"] : []),
      "Invoices",
      ...(receipts ? [] : ["Expected"]),
      "Received",
      ...(receipts ? [] : ["Outstanding"]),
      "Is dues",
    ]),
    ...(d.bySource ?? []).map((r: any) =>
      line([
        d.sourceLabels[r.source] ?? r.source,
        ...(receipts ? [r.payments ?? 0] : []),
        r.count ?? 0,
        ...(receipts ? [] : [r.is_dues ? naira(r.due) : "—"]),
        naira(r.paid),
        ...(receipts ? [] : [r.is_dues ? naira(r.balance) : "—"]),
        r.is_dues ? "Yes" : "No",
      ]),
    ),
  ];

  if ((d.byStatus ?? []).length) {
    lines.push(
      "",
      line(["By invoice status"]),
      line(["Status", "Invoices", "Expected", "Received", "Outstanding"]),
      ...d.byStatus.map((r: any) =>
        line([r.status, r.count ?? 0, naira(r.due), naira(r.paid), naira(r.balance)]),
      ),
    );
  }

  if (d.breakdown) {
    const b = d.breakdown;

    lines.push(
      "",
      line([`${d.sourceLabels[b.source] ?? b.source} by ${String(b.dimension).toLowerCase()}`]),
      line([
        b.dimension,
        ...(isEvents ? ["Registrants", "Guests"] : ["Invoices"]),
        ...(receivedOnly ? [] : ["Expected"]),
        "Received",
        ...(receivedOnly ? [] : ["Outstanding"]),
      ]),
      ...(b.rows ?? []).map((r: any) =>
        line([
          r.label,
          ...(isEvents ? [r.registrants ?? 0, r.guests ?? 0] : [r.count ?? 0]),
          ...(receivedOnly ? [] : [naira(r.due)]),
          naira(r.paid),
          ...(receivedOnly ? [] : [naira(r.balance)]),
        ]),
      ),
    );

    if (b.truncated) {
      lines.push(line(["Note", "Top 200 rows only — narrow the filters for the rest"]));
    }
  }

  if (d.payments?.length) {
    lines.push("", ...paymentLines(d));
  }

  download(lines.join("\n"), "text/csv;charset=utf-8;", filename(d.meta, "csv"));
}

/** The transaction list on the printed sheet, or nothing where none was taken. */
function paymentsBlock(d: FinanceExportData): string {
  const rows = d.payments ?? [];

  if (!rows.length) {
    return "";
  }

  const listed = rows.reduce((sum, r) => sum + Number(r.allocated || 0), 0);
  const received = Number(d.summary?.total_paid || 0);

  return `<h2>Payments (${rows.length.toLocaleString()})</h2>
    <table>
      <thead><tr>
        <th>Paid on</th><th>Reference</th><th>Receipt</th><th>Payer</th>
        <th>Membership no</th><th>Invoice</th><th>Method</th>
        <th class="n">Amount</th><th class="n">Counted here</th>
      </tr></thead>
      <tbody>${rows
        .map(
          (r: any) => `<tr>
            <td>${esc(day(r.paid_at))}</td>
            <td>${esc(r.reference ?? "")}</td>
            <td>${esc(r.receipt_number ?? "")}</td>
            <td>${esc(r.payer ?? "")}</td>
            <td>${esc(r.membership_no ?? "")}</td>
            <td>${esc(r.invoice_number ?? "")}</td>
            <td>${esc([r.method, r.gateway].filter(Boolean).join(" / "))}</td>
            <td class="n">${money(r.amount)}</td>
            <td class="n">${money(r.allocated)}</td>
          </tr>`,
        )
        .join("")}</tbody>
      <tfoot><tr>
        <td colspan="7"><b>Listed total</b></td>
        <td class="n"></td><td class="n"><b>${money(listed)}</b></td>
      </tr></tfoot>
    </table>
    ${
      d.paymentsTruncated
        ? `<p class="note">The most recent ${rows.length.toLocaleString()} payments only — narrow the filters to list the rest. The totals above cover everything.</p>`
        : ""
    }
    ${
      !d.paymentsTruncated && Math.abs(listed - received) > 1
        ? '<p class="note">The listed payments do not add up to Received: an invoice migrated from the old register can carry a paid amount with no payment record behind it.</p>'
        : ""
    }`;
}

/* ── Printed sheet ───────────────────────────────────────────────────────── */

/**
 * Opened in a new window and printed from there, so the browser's own "save as
 * PDF" does the conversion. No PDF library is pulled into the bundle for a
 * report that is printed a handful of times a month.
 */
export function exportFinanceAnalysisPdf(d: FinanceExportData) {
  const s = d.summary ?? {};
  const receipts = s.basis === "receipts";
  const receivedOnly = receipts || d.meta.receivedOnly;
  const isEvents = d.breakdown?.source === "event_fee";
  const { printed } = stamp();

  const filterLine = Object.entries(d.meta.filters)
    .map(([k, v]) => `${esc(k)}: <b>${esc(v)}</b>`)
    .join(" &middot; ");

  const tile = (label: string, value: string) =>
    `<div class="tile"><div class="k">${esc(label)}</div><div class="v">${esc(value)}</div></div>`;

  const sourceRows = (d.bySource ?? [])
    .map(
      (r: any) => `<tr>
        <td>${esc(d.sourceLabels[r.source] ?? r.source)}${r.is_dues ? "" : ' <span class="tag">not dues</span>'}</td>
        ${receipts ? `<td class="n">${Number(r.payments || 0).toLocaleString()}</td>` : ""}
        <td class="n">${Number(r.count || 0).toLocaleString()}</td>
        ${receipts ? "" : `<td class="n">${r.is_dues ? money(r.due) : "—"}</td>`}
        <td class="n">${money(r.paid)}</td>
        ${receipts ? "" : `<td class="n">${r.is_dues ? money(r.balance) : "—"}</td>`}
      </tr>`,
    )
    .join("");

  const breakdownBlock = d.breakdown
    ? `<h2>${esc(d.sourceLabels[d.breakdown.source] ?? d.breakdown.source)} by ${esc(
        String(d.breakdown.dimension).toLowerCase(),
      )}</h2>
      <table>
        <thead><tr>
          <th>${esc(d.breakdown.dimension)}</th>
          ${isEvents ? "<th class='n'>Registrants</th><th class='n'>Guests</th>" : "<th class='n'>Invoices</th>"}
          ${receivedOnly ? "" : "<th class='n'>Expected</th>"}
          <th class="n">Received</th>
          ${receivedOnly ? "" : "<th class='n'>Outstanding</th>"}
        </tr></thead>
        <tbody>${(d.breakdown.rows ?? [])
          .map(
            (r: any) => `<tr>
              <td>${esc(r.label)}</td>
              ${
                isEvents
                  ? `<td class="n">${Number(r.registrants || 0).toLocaleString()}</td><td class="n">${Number(r.guests || 0).toLocaleString()}</td>`
                  : `<td class="n">${Number(r.count || 0).toLocaleString()}</td>`
              }
              ${receivedOnly ? "" : `<td class="n">${money(r.due)}</td>`}
              <td class="n">${money(r.paid)}</td>
              ${receivedOnly ? "" : `<td class="n">${money(r.balance)}</td>`}
            </tr>`,
          )
          .join("")}</tbody>
      </table>
      ${d.breakdown.truncated ? '<p class="note">Top 200 rows only — narrow the filters to see the rest.</p>' : ""}`
    : "";

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>NIQS — Finance Analysis</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  *{box-sizing:border-box}
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:10px;color:#111;margin:0}
  .org{font-size:9px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:#888}
  h1{font-size:16px;margin:4px 0 2px}
  .sub{font-size:9px;color:#666;line-height:1.5}
  h2{font-size:12px;margin:16px 0 6px;padding-bottom:3px;border-bottom:1px solid #eee}
  .tiles{display:flex;gap:8px;margin:10px 0 2px;flex-wrap:wrap}
  .tile{border:1px solid #e5e5e5;border-radius:6px;padding:6px 10px;min-width:120px}
  .tile .k{font-size:8px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#888}
  .tile .v{font-size:13px;font-weight:800;margin-top:2px}
  table{width:100%;border-collapse:collapse;margin-top:4px}
  th,td{border-bottom:1px solid #eee;padding:4px 6px;text-align:left;vertical-align:top}
  th{background:#f6f6f6;font-size:8px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#666}
  td.n,th.n{text-align:right;font-variant-numeric:tabular-nums}
  thead{display:table-header-group}
  tr{page-break-inside:avoid}
  .tag{font-size:7px;font-weight:800;text-transform:uppercase;color:#8a6d00;border:1px solid #e8d9a0;background:#fdf8e6;border-radius:3px;padding:1px 3px}
  .note{font-size:8px;color:#8a6d00;margin-top:4px}
  .foot{margin-top:14px;padding-top:6px;border-top:1px solid #eee;font-size:8px;color:#aaa}
</style></head>
<body>
  <div class="org">Nigerian Institute of Quantity Surveyors</div>
  <h1>Finance Analysis${d.meta.sourceLabel ? ` — ${esc(d.meta.sourceLabel)}` : ""}</h1>
  <div class="sub">
    ${filterLine || "No filters — the whole period"}<br>
    Taken on ${esc(printed)} &middot; amounts in naira &middot; received counts successful payments only<br>
    ${
      receipts
        ? "Money <b>received</b> in the window, by the date each payment was made. Expected and outstanding are not reported for a date range — they are positions, not a flow."
        : receivedOnly
          ? "Expected and outstanding are not reported for this source: a voluntary fee is not a debt."
          : "Expected and outstanding cover annual dues and the development levy only."
    }
  </div>

  <div class="tiles">
    ${receivedOnly ? "" : tile("Total expected", money(s.total_expected ?? s.total_due))}
    ${tile("Received", money(s.total_paid))}
    ${receivedOnly ? "" : tile("Outstanding", money(s.total_balance))}
    ${receivedOnly ? "" : tile("Collection rate", `${s.collection_rate ?? 0}%`)}
    ${receipts ? tile("Payments", Number(s.payments || 0).toLocaleString()) : ""}
    ${tile("Invoices", Number(s.invoices || 0).toLocaleString())}
    ${tile("Members", Number(s.members || 0).toLocaleString())}
  </div>

  <h2>${receipts ? "Money received, by source" : "Income by source"}</h2>
  <table>
    <thead><tr>
      <th>Source</th>
      ${receipts ? '<th class="n">Payments</th>' : ""}
      <th class="n">Invoices</th>
      ${receipts ? "" : '<th class="n">Expected</th>'}
      <th class="n">Received</th>
      ${receipts ? "" : '<th class="n">Outstanding</th>'}
    </tr></thead>
    <tbody>${
      sourceRows ||
      `<tr><td colspan="${receipts ? 4 : 5}">${
        receipts
          ? "No money came in under these filters."
          : "Nothing billed under these filters."
      }</td></tr>`
    }</tbody>
  </table>

  ${breakdownBlock}

  ${paymentsBlock(d)}

  <div class="foot">NIQS Finance Analysis &middot; generated from the portal</div>
  <script>window.onload = function () { window.print(); };</script>
</body></html>`;

  const win = window.open("", "_blank");

  if (!win) {
    // Pop-up blocked. Hand over the same sheet as a file rather than failing
    // silently — the browser will open it, and print still works from there.
    download(html, "text/html;charset=utf-8;", filename(d.meta, "html"));
    return;
  }

  win.document.write(html);
  win.document.close();
}
